import { Grid, Stack } from '@mui/material'
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import TabList from 'common/components/tab/TabList'
import { isNil, keys, round } from 'lodash'
import Input from 'common/components/form/Input'
import { Fieldset, Legend } from 'common/components/form/Fieldset'
import { formatMilliers } from 'common/utils/StringUtil'
import { convertBytesToBG } from 'common/utils/NumberUtil'
import { getFullDate } from 'common/utils/DateUtil'

const TABS = {
    SERVER: 1,
    JVM: 2,
    THREADS: 3,
    DISK: 4,
    GC: 5,
    DB: 6,
    JSON: 7,
}

const ServerInfosCard = ({
    data = {},
    headerComponent,
}) => {
    const infos = data?.supervisionValues || {}
    const dbMetrics = infos?.dbMetrics || {}
    const [tab, setTab] = useState(TABS.SERVER)

    return (
        <TabList
            controlTab={tab}
            setControlTab={setTab}
            reverse
            headerComponent={headerComponent}
            tabs={[{
                constant: TABS.SERVER,
                label: 'Serveur',
                icon: 'dns',
            }, {
                constant: TABS.JVM,
                label: 'JVM',
                icon: 'view_in_ar',
            }, {
                constant: TABS.THREADS,
                label: 'Threads',
                icon: 'terminal',
            }, {
                constant: TABS.DISK,
                label: 'Disque',
                icon: 'storage',
            }, {
                constant: TABS.GC,
                label: 'Garbage Collector',
                icon: 'delete_sweep',
            }, {
                constant: TABS.DB,
                label: 'Base de données',
                icon: 'dataset',
            }, {
                constant: TABS.JSON,
                label: 'JSON',
                icon: 'article',
            }]}
        >
            {t => (
                <>
                    {t === TABS.SERVER && (
                        <Grid container spacing={1}>
                            <Grid size={4}>
                                <Input
                                    label='System CPU Load'
                                    value={`${round(infos?.os?.systemCpuLoad * 100, 2)} %`}
                                    tooltip='Charge du système entier (total de tous les process)'
                                    disabled
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Process CPU Load'
                                    value={`${round(infos?.os?.processCpuLoad * 100, 2)} %`}
                                    tooltip='Pourcentage de charge du CPU pour le process'
                                    disabled
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Available Processors'
                                    value={infos.availableProcessors}
                                    disabled
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Free Physical Memory'
                                    value={`${formatMilliers(convertBytesToBG(infos?.os?.freePhysicalMem))} Go`}
                                    tooltip={'Si free reste bas trop longtemps, c\'est pas normal, il manque du CPU'}
                                    disabled
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Total Physical Memory'
                                    value={`${formatMilliers(convertBytesToBG(infos?.os?.totalPhysicalMem))} Go`}
                                    tooltip='RAM max de la machine'
                                    disabled
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Last Backup'
                                    value={infos.backup?.split(' - ')?.[1]}
                                    disabled
                                />
                            </Grid>
                            <Grid size={12}>
                                <Fieldset>
                                    <Legend>File descriptors (Unix)</Legend>
                                    <Grid container spacing={1}>
                                        <Grid size={6}>
                                            <Input
                                                label='Ouvert'
                                                value={infos?.fileDescriptors?.open}
                                                disabled
                                                tooltip={'Nb de fichiers/sockets ouverts par le process.\nDoit rester à peu près stable.\nAugmente sans redescendre → fuite de connexions/sockets. Si approche trop du max, risque d\'erreur "Too many open files"'}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Input
                                                label='Max'
                                                value={infos?.fileDescriptors?.max}
                                                disabled
                                                tooltip={'Limite de l\'OS (ulimit)'}
                                            />
                                        </Grid>
                                    </Grid>
                                </Fieldset>
                            </Grid>
                        </Grid>
                    )}
                    {t === TABS.JVM && (
                        <Grid container spacing={1}>
                            <Grid size={6}>
                                <Fieldset>
                                    <Legend>Heap</Legend>
                                    <Input
                                        label='Utilisé'
                                        value={`${formatMilliers(convertBytesToBG(infos?.jvmHeap?.used))} GB`}
                                        disabled
                                        tooltip={'Heap réellement utilisé par les objets vivants, Le GC libère régulièrement la mémoire, donc normal si ça monte/descend\nPas normal si ça monte sans jamais redescendre ou si souvent > 80/90% du max'}
                                    />
                                    <Input
                                        label='Alloué'
                                        value={`${formatMilliers(convertBytesToBG(infos?.jvmHeap?.committed))} GB`}
                                        disabled
                                        tooltip={'Heap “réservé/alloué” par la JVM auprès de l\'OS (peut grandir).\nMonte au début et stable, mais si c\'est proche du max aussi alors pas normal'}
                                    />
                                    <Input
                                        label='Max'
                                        value={`${formatMilliers(convertBytesToBG(infos?.jvmHeap?.max))} GB`}
                                        disabled
                                        tooltip='-Xmx'
                                    />
                                </Fieldset>
                            </Grid>
                            <Grid size={6}>
                                <Fieldset>
                                    <Legend>Non Heap</Legend>
                                    <Input
                                        label='Utilisé'
                                        value={`${formatMilliers(convertBytesToBG(infos?.jvmNonHeap?.used))} GB`}
                                        disabled
                                        tooltip={'Mémoire hors heap : Metaspace (classes), CodeCache (JIT), etc.\nMonte au démarrage puis se stabilise.\nProblème si croissance continue → souvent chargements de classes non maîtrisés (plugins, classloaders), ou fuite Metaspace.'}
                                    />
                                    <Input
                                        label='Alloué'
                                        value={`${formatMilliers(convertBytesToBG(infos?.jvmNonHeap?.committed))} GB`}
                                        disabled
                                        tooltip={'Non heap alloué'}
                                    />
                                </Fieldset>
                            </Grid>
                        </Grid>
                    )}
                    {t === TABS.THREADS && (
                        <Grid container spacing={1}>
                            <Grid size={4}>
                                <Input
                                    label='Count'
                                    value={infos?.threads?.count}
                                    disabled
                                    tooltip={'Nb threads vivants, normalement toujours stable, varie très peu.\nSi ça augmente tout le temps --> fuite de threads (création non bornée, scheduler mal géré).\nProblème : très élevé (centaines/milliers) → overhead, context switching, risques de saturation.'}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Peak'
                                    value={infos?.threads?.peak}
                                    disabled
                                    tooltip={'Max historique depuis le démarrage.\nProblème : peak énorme vs count actuel → il y a eu un épisode (burst) potentiellement lié à latence/timeouts.'}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Deadlock'
                                    value={infos?.threads?.deadlock}
                                    disabled
                                    tooltip='> 0 = incident (blocage permanent) → besoin de thread dump et correction.'
                                />
                            </Grid>
                        </Grid>
                    )}
                    {t === TABS.DISK && (
                        <Grid container spacing={1}>
                            <Grid size={4}>
                                <Input
                                    label='Store Name'
                                    value={infos?.disk?.storeName}
                                    disabled
                                    tooltip={'Nb threads vivants, normalement toujours stable, varie très peu.\nSi ça augmente tout le temps --> fuite de threads (création non bornée, scheduler mal géré).\nProblème : très élevé (centaines/milliers) → overhead, context switching, risques de saturation.'}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Type'
                                    value={infos?.disk?.type}
                                    disabled
                                    tooltip={'Max historique depuis le démarrage.\nProblème : peak énorme vs count actuel → il y a eu un épisode (burst) potentiellement lié à latence/timeouts.'}
                                />
                            </Grid>
                            <Grid size={4} />
                            <Grid size={4}>
                                <Input
                                    label='Total Bytes'
                                    value={`${formatMilliers(convertBytesToBG(infos?.disk?.totalBytes))} GB`}
                                    disabled
                                    tooltip='Stockage total'
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Usable Bytes'
                                    value={`${formatMilliers(convertBytesToBG(infos?.disk?.usableBytes))} GB`}
                                    disabled
                                    tooltip={'Ce que TON process peut utiliser (droits/quotas)'}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    label='Free Bytes'
                                    value={`${formatMilliers(convertBytesToBG(infos?.disk?.freeBytes))} GB`}
                                    disabled
                                    tooltip={'Libre “brut” sur le volume'}
                                />
                            </Grid>
                        </Grid>
                    )}
                    {t === TABS.GC && (
                        <Stack spacing={1}>
                            {infos?.gc?.map(gc => (
                                <Fieldset key={gc.name}>
                                    <Legend>{gc.name}</Legend>
                                    <Grid container spacing={1}>
                                        {keys(gc).filter(k => k !== 'name').map(k => (
                                            <Grid size={4} key={k}>
                                                <Input label={k} value={gc[k]} disabled />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Fieldset>
                            ))}
                        </Stack>
                    )}
                    {t === TABS.DB && (
                        <Stack spacing={1}>
                            <Fieldset>
                                <Legend>Idle in transaction</Legend>
                                <Grid container spacing={1}>
                                    <Grid size={6}>
                                        <Input
                                            label='Count'
                                            value={dbMetrics?.idleInTransaction?.count}
                                            disabled
                                            error={dbMetrics?.idleInTransaction?.count > 0 && dbMetrics?.idleInTransaction?.maxAgeSeconds > 300}
                                            tooltip={'Nb de sessions actuellement "idle in transaction".\nAttendu : 0 en régime nominal.\nAlerte dès 1 si l\'âge max dépasse quelques minutes.'}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <Input
                                            label='Âge max (s)'
                                            value={dbMetrics?.idleInTransaction?.maxAgeSeconds}
                                            disabled
                                            error={dbMetrics?.idleInTransaction?.maxAgeSeconds > 1800}
                                            tooltip={'Âge de la plus vieille session idle-in-transaction.\n< 60s : transitoire (souvent bénin)\n60-300s : anormal, à surveiller\n> 1800s (30 min) : incident — bug applicatif, autovacuum bloqué, timeouts RabbitMQ consumer\n> 1 jour : équivalent du bug du 18/04 (transaction idle 4 jours + VACUUM FULL)'}
                                        />
                                    </Grid>
                                </Grid>
                            </Fieldset>

                            <Fieldset>
                                <Legend>Long running queries</Legend>
                                <Grid container spacing={1}>
                                    <Grid size={4}>
                                        <Input
                                            label='Count'
                                            value={dbMetrics?.longRunningQueries?.count}
                                            disabled
                                            error={dbMetrics?.longRunningQueries?.count > 2}
                                            tooltip={'Nb de requêtes actives depuis plus que le seuil.\nAttendu : 0 en régime nominal.\n1-2 ponctuel : acceptable (gros export, calcul lourd).\nPlusieurs en simultané : charge anormale ou saturation du pool Hikari.'}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <Input
                                            label='Âge max (s)'
                                            value={dbMetrics?.longRunningQueries?.maxAgeSeconds}
                                            disabled
                                            error={dbMetrics?.longRunningQueries?.maxAgeSeconds > 600}
                                            tooltip={'Âge de la plus vieille requête active.\nProche du seuil : banal.\n> 600s (10 min) : requête probablement cassée (plan sous-optimal, stats obsolètes, lock contesté).\n> 1800s (30 min) : timeouts RabbitMQ consumer et frontend.'}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <Input
                                            label='Seuil (s)'
                                            value={dbMetrics?.longRunningQueries?.thresholdSeconds}
                                            disabled
                                            tooltip='Seuil utilisé pour définir "long running".'
                                        />
                                    </Grid>
                                </Grid>
                            </Fieldset>
                            {dbMetrics?.tables?.map(table => (
                                <Fieldset key={table.name}>
                                    <Legend>{table.name}</Legend>
                                    <Grid container spacing={1}>
                                        <Grid size={4}>
                                            <Input
                                                label='Live tuples'
                                                value={formatMilliers(table.liveTuples)}
                                                disabled
                                                tooltip='Nb de lignes vivantes (visibles) estimées. Ordre de grandeur pour dimensionnement.'
                                            />
                                        </Grid>
                                        <Grid size={4}>
                                            <Input
                                                label='Dead tuples'
                                                value={formatMilliers(table.deadTuples)}
                                                disabled
                                                tooltip={'Nb de lignes mortes (UPDATE/DELETE) en attente de VACUUM.\nCoexistent dans les pages → ralentissent les scans.'}
                                            />
                                        </Grid>
                                        <Grid size={4}>
                                            <Input
                                                label='Dead %'
                                                value={isNil(table.deadPct) ? '-' : `${table.deadPct} %`}
                                                disabled
                                                error={table.deadPct > 25}
                                                tooltip={'Pourcentage de bloat = deadTuples / (live + dead).\n< 5% : propre, autovacuum fait son job\n5-15% : normal sur table chaude, transitoire\n15-25% : autovacuum prend du retard\n> 25% : incident — SELECT/INDEX dégradés. Lancer un VACUUM manuel et investiguer.'}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Input
                                                label='Dernier autovacuum'
                                                value={table.lastAutovacuum ? getFullDate(table.lastAutovacuum) : '-'}
                                                disabled
                                                tooltip={'Doit être < 24h sur tables chaudes (seuil 5%).\n> 3 jours : autovacuum ne tourne pas — potentielle transaction longue qui le bloque, ou param global désactivé.'}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Input
                                                label='Dernier autoanalyze'
                                                value={table.lastAutoanalyze ? getFullDate(table.lastAutoanalyze) : '-'}
                                                disabled
                                                tooltip={'Doit être < 12h (seuil 2%).\n> 2 jours : risque fort de plans sous-optimaux (Seq Scan au lieu d\'Index Scan) → requêtes qui explosent en temps.'}
                                            />
                                        </Grid>
                                    </Grid>
                                </Fieldset>
                            ))}
                        </Stack>
                    )}
                    {t === TABS.JSON && (
                        <pre>
                            {JSON.stringify(data, null, 2)}
                        </pre>
                    )}
                </>
            )}
        </TabList>
    )
}

ServerInfosCard.propTypes = {
    data: PropTypes.shape({}),
    headerComponent: PropTypes.element,
}

export default ServerInfosCard